import { ResponsiveLine } from '@nivo/line';
import { icon_tooltip } from 'components/base/images';
import _ from 'lodash';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { _font } from 'styles/_variables';
import { color } from 'styles/utils';
import AxisBottomRenderTick from './AxisBottomRenderTick';

export default function LineChart(props) {
  let {
    width = 1140,
    height = 270,
    isMultipleData = false,
    isRenderTick = false,
    data = [],
    legendsData = [],
    hasLegendsComponent = false,
    legendsComponent,
    className = '',
    enableArea = false,
    margin = {},
    axisBottom = {},
  } = props;
  const containerStyle = {
    width,
    height,
  };
  const chartBoxRef = useRef();
  const legendsBoxRef = useRef();
  const [chartContainerHeight, setChartContainerHeight] = useState(height);
  const maxValue = useMemo(() => {
    let exceedValue = null;
    if (isMultipleData) {
      data.forEach(item => {
        if (!Array.isArray(item?.data)) return;
        const value = item.data.find(dataItem => dataItem.y > 10);

        if (!!value) exceedValue = value;
      });
    } else {
      if (!Array.isArray(data)) return;
      const value = data.find(dataItem => dataItem.y > 10);

      if (!!value) exceedValue = value;
    }

    return exceedValue ? 'auto' : 10;
  }, [data]);

  useEffect(() => {
    console.log('maxValue ______', maxValue);
  }, [maxValue]);

  useLayoutEffect(() => {
    if (legendsBoxRef.current) {
      setChartContainerHeight(height + legendsBoxRef.current.clientHeight);
    }
  }, [height, legendsBoxRef.current?.height]);

  const theme = {
    fontFamily: `${_font.roboto}`,
    textColor: '#555',
    axis: {
      ticks: {
        text: {
          fontSize: 12,
        },
        line: {
          stroke: '#E2E7EA',
        },
      },
    },
    legends: {
      text: {
        fontSize: 14,
      },
    },
    grid: {
      line: {
        stroke: '#E2E7EA',
        strokeDasharray: 1,
      },
    },
    crosshair: {
      line: {
        stroke: '#0F3F62',
        strokeWidth: 0,
        strokeHeight: 100,
        strokeDasharray: 0,
      },
    },
  };

  const legendsConfig = legendsData
    ? []
    : [
        {
          anchor: 'bottom-left',
          direction: 'row',
          translateX: -50,
          translateY: 100,
          itemsSpacing: 5,
          itemDirection: 'left-to-right',
          itemWidth: 110,
          itemHeight: 20,
          symbolSize: 14,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          itemTextColor: '#333',
          effects: [],
        },
      ];

  const CustomPoint = props => {
    const { currentPoint, borderWidth, borderColor } = props;
    // it will show the current point

    if (currentPoint) {
      return (
        <>
          <filter id="shadow">
            <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="0.1" floodColor="black" />
          </filter>
          <g>
            <circle
              fill={currentPoint?.color}
              r={10}
              strokeWidth={3}
              stroke="#fff"
              cx={currentPoint.x}
              cy={currentPoint.y}
              filter="url(#shadow)"
            />
          </g>
        </>
      );
    } else {
      return <></>;
    }
  };

  return (
    <Styled.LineChart
      style={{
        ...containerStyle,
        height: chartContainerHeight,
      }}
      data-component-name="LineChart"
      className={className}
    >
      <div className="chart__box" ref={chartBoxRef} style={containerStyle}>
        {!data.length && (
          <div className="chart__no_data" style={containerStyle}>
            Select valid type.
          </div>
        )}
        {!!data.length && (
          <ResponsiveLine
            theme={theme}
            data={data}
            // margin={{ top: 25, right: 25, bottom: 90, left: 70 }}
            margin={{ top: 25, right: 25, bottom: 50, left: 70, ...margin }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: maxValue, stacked: false, reverse: false }}
            enableGridY={false}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              orient: 'bottom',
              tickSize: 0,
              tickPadding: 15,
              tickRotation: -60,
              legendPosition: 'middle',
              ...axisBottom,
              renderTick: props => (
                <AxisBottomRenderTick config={{ ...props, isRenderTick, theme }} />
              ),
            }}
            axisLeft={{
              orient: 'left',
              tickSize: 0,
              tickPadding: 30,
              tickRotation: 0,
              legendPosition: 'middle',
              format: e => Math.floor(e) === e && e,
            }}
            colors={data => data.color}
            enableArea={enableArea}
            enablePoints={false}
            pointColor={{ from: 'color', modifiers: [] }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            crosshairType="x"
            tooltip={data => (
              <>
                {_.map(data, (e, i) => {
                  return (
                    <div key={i} className="chart__tooltip">
                      <div className="chart__tooltip_img">
                        <img src={icon_tooltip} />
                      </div>
                      <div className="chart__tooltip_data">{e.data.y}</div>
                    </div>
                  );
                })}
              </>
            )}
            useMesh={true}
            // NOTE: 자동너비 불가로 direction row일 경우 커스텀으로 구현
            // NOTE: 기존거 사용시 data에 id 값
            legends={legendsConfig}
            motionStiffness={200}
            motionDamping={40}
            layers={[
              'grid',
              'markers',
              'axes',
              'areas',
              'crosshair',
              'lines',
              CustomPoint,
              'slices',
              'mesh',
              'legends',
            ]}
          />
        )}

        <div className="chart__legends_box" ref={legendsBoxRef}>
          {hasLegendsComponent && (
            <>
              {legendsComponent ? (
                <>{legendsComponent}</>
              ) : (
                <div className="chart__legends cf">
                  {legendsData.map((item, idx) => (
                    <div className="chart__legends_item" key={idx}>
                      <span
                        className="chart__legends_symbol"
                        style={{ backgroundColor: item.color }}
                      ></span>
                      <span className="chart__legends_name">{item.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Styled.LineChart>
  );
}

const Styled = {
  LineChart: styled.div`
    position: relative;
    margin: 0 auto;
    background-color: #fff;
    & {
      .chart {
        width: 100%;
        height: 100%;
      }
      .chart__tooltip {
        position: relative;
        top: 20px;
        left: 25px;
        padding: 7px 8px;
        color: #fff;
        .chart__tooltip_img {
          width: 100%;
          vertical-align: middle;
        }
        .chart__tooltip_data {
          padding: 0px 10px 10px 10px;
          text-align: center;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #25256f;
        }
      }
      .chart__legends_box {
        position: relative;
        padding: 0 25px;
      }
      .chart__legends {
        .chart__legends_item {
          display: inline-flex;
          align-items: center;
          &:not(:first-child) {
            margin-left: 12px;
          }
          .chart__legends_symbol {
            display: inline-block;
            width: 14px;
            height: 14px;
            border-radius: 50%;
          }
          .chart__legends_name {
            margin-left: 5px;
            font-size: 14px;
            color: #333;
          }
        }
      }
      .chart__no_data {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 20px;
        color: ${color.gray_week_font};
      }
    }
  `,
};
